import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { CameraFit, CameraKind, StreamConfig, ThumbRect } from '../../shared/types'
import { enabledCameras } from '../../shared/types'
import {
  clampThumbRect,
  computeGrid,
  gridOffset,
  gridRects,
  planSpotlight
} from '../../shared/layout'

/**
 * The composition, arranged by hand. Every tile is the camera that will occupy
 * that spot on air, computed with the same maths as the filter graph.
 *
 * Grid mode: dragging one tile onto another swaps the two cameras in the
 * config — the wall order IS the camera order, so the swap is visible
 * everywhere else too.
 *
 * Spotlight mode: each mini moves and resizes independently. Dragging one
 * writes a per-camera rect into `spotlightRects`; a mini never dragged stays in
 * the automatic strip. Double-clicking a mini puts it in the spotlight.
 */

const KIND_TAGS: Record<CameraKind, string> = {
  rtsp: 'RTSP',
  device: 'USB',
  file: 'VIDEO',
  url: 'WEB',
  picture: 'PIC'
}

/** Snap pitch on the output canvas, in output pixels. */
const SNAP = 16
/** Pull-to-edge distance, in output pixels. */
const EDGE = 12

interface Cell {
  /** Index into the enabled-camera list — the same index the filter graph uses. */
  cam: number
  x: number
  y: number
  w: number
  h: number
  main: boolean
  custom: boolean
}

interface Drag {
  kind: 'swap' | 'move' | 'resize'
  from: number
  /** Pointer position at the start, board px. */
  px: number
  py: number
  /** The cell's rect at the start, output px. */
  origin: ThumbRect
  /** Live rect while moving/resizing, output px. */
  live: ThumbRect
  moved: boolean
}

interface Props {
  config: StreamConfig
  locked: boolean
  onPatch: (changes: Partial<StreamConfig>) => void
}

export default function CompositionBoard({ config, locked, onPatch }: Props): React.JSX.Element {
  const boardRef = useRef<HTMLDivElement>(null)
  const [boardW, setBoardW] = useState(440)
  // The ref is the source of truth: down/move/up can all land inside one React
  // batch (fast drags, automation), where state set by an earlier handler is
  // not yet visible to a later one. State mirrors it purely for rendering.
  const dragRef = useRef<Drag | null>(null)
  const targetRef = useRef<number | null>(null)
  const [drag, setDrag] = useState<Drag | null>(null)
  const [target, setTarget] = useState<number | null>(null)
  const [snap, setSnap] = useState(true)

  const cameras = enabledCameras(config)
  const spotlight = config.mode === 'spotlight'
  const scale = boardW / config.outputWidth
  const boardH = Math.round(config.outputHeight * scale)
  const placed = Object.keys(config.spotlightRects ?? {}).length > 0

  /**
   * Track the real rendered width so the board can fill the card on any window.
   *
   * Measured in a layout effect and again on every resize, and a zero reading is
   * ignored rather than treated as a width: mounted inside a hidden panel (the
   * console's config modal, a tab that is not showing) `clientWidth` is 0, and
   * taking the placeholder as truth left every tile drawn at the wrong scale
   * inside a correctly-sized frame — an arrangement that did not match the one
   * going on air.
   */
  useLayoutEffect(() => {
    const el = boardRef.current
    if (!el) return
    const read = (): void => {
      const w = el.clientWidth
      if (w > 0) setBoardW(w)
    }
    read()
    const observer = new ResizeObserver(read)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const cells = useMemo<Cell[]>(() => {
    const out: Cell[] = []
    if (!spotlight) {
      const grid = computeGrid(
        cameras.length,
        config.layout,
        config.outputWidth,
        config.outputHeight
      )
      const rects = gridRects(cameras.length, grid, config)
      const off = gridOffset(rects.boxW, rects.boxH, config)
      rects.cells.forEach((cell, i) =>
        out.push({
          cam: i,
          x: off.x + cell.x,
          y: off.y + cell.y,
          w: grid.tileW,
          h: grid.tileH,
          main: false,
          custom: false
        })
      )
    } else {
      const plan = planSpotlight(
        config,
        cameras.map((c) => c.id)
      )
      out.push({
        cam: plan.main,
        x: 0,
        y: 0,
        w: config.outputWidth,
        h: config.outputHeight,
        main: true,
        custom: false
      })
      for (const t of plan.thumbs)
        out.push({ cam: t.cam, x: t.x, y: t.y, w: t.w, h: t.h, main: false, custom: t.custom })
    }
    return out
  }, [cameras, config, spotlight])

  /** The cell under a board-relative point — thumbs sit over the background, so scan topmost first. */
  const hit = (px: number, py: number): number | null => {
    for (let i = cells.length - 1; i >= 0; i--) {
      const c = cells[i]
      const x = c.x * scale
      const y = c.y * scale
      if (px >= x && px < x + c.w * scale && py >= y && py < y + c.h * scale) return i
    }
    return null
  }

  const boardPoint = (e: React.PointerEvent): { x: number; y: number } => {
    const rect = boardRef.current!.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  /** Grid pitch first, then a stronger pull onto the canvas edges. */
  const snapRect = (r: ThumbRect, resize: boolean): ThumbRect => {
    let { x, y, w, h } = r
    if (snap) {
      const q = (n: number): number => Math.round(n / SNAP) * SNAP
      if (resize) {
        w = q(w)
        h = q(h)
      } else {
        x = q(x)
        y = q(y)
      }
    }
    const W = config.outputWidth
    const H = config.outputHeight
    if (!resize) {
      if (Math.abs(x) < EDGE) x = 0
      if (Math.abs(x + w - W) < EDGE) x = W - w
      if (Math.abs(y) < EDGE) y = 0
      if (Math.abs(y + h - H) < EDGE) y = H - h
    } else {
      if (Math.abs(x + w - W) < EDGE) w = W - x
      if (Math.abs(y + h - H) < EDGE) h = H - y
      // Growing past the canvas caps the size — clampThumbRect would otherwise
      // keep the size and shove the mini leftward/upward instead.
      w = Math.min(w, W - x)
      h = Math.min(h, H - y)
    }
    return clampThumbRect({ x, y, w, h }, config)
  }

  const capture = (e: React.PointerEvent): void => {
    try {
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    } catch {
      /* synthetic pointers have no capturable id; dragging still works */
    }
  }

  const begin = (e: React.PointerEvent, index: number, kind: Drag['kind']): void => {
    if (locked) return
    e.preventDefault()
    e.stopPropagation()
    capture(e)
    const p = boardPoint(e)
    const c = cells[index]
    dragRef.current = {
      kind,
      from: index,
      px: p.x,
      py: p.y,
      origin: { x: c.x, y: c.y, w: c.w, h: c.h },
      live: { x: c.x, y: c.y, w: c.w, h: c.h },
      moved: false
    }
    targetRef.current = null
    setDrag(dragRef.current)
    setTarget(null)
  }

  const down = (e: React.PointerEvent, index: number): void => {
    const cell = cells[index]
    if (!cell || cells.length < 2) return
    // The spotlight background is the canvas itself — nothing to drag.
    if (cell.main) return
    begin(e, index, spotlight ? 'move' : 'swap')
  }

  const move = (e: React.PointerEvent): void => {
    const d = dragRef.current
    if (!d) return
    const p = boardPoint(e)
    const moved = d.moved || Math.hypot(p.x - d.px, p.y - d.py) > 4
    // Pointer delta in output pixels.
    const dx = (p.x - d.px) / scale
    const dy = (p.y - d.py) / scale
    let live = d.live
    if (d.kind === 'move') {
      live = snapRect({ ...d.origin, x: d.origin.x + dx, y: d.origin.y + dy }, false)
    } else if (d.kind === 'resize') {
      live = snapRect({ ...d.origin, w: d.origin.w + dx, h: d.origin.h + dy }, true)
    }
    dragRef.current = { ...d, live, moved }
    setDrag(dragRef.current)
    if (d.kind === 'swap' && moved) {
      const over = hit(p.x, p.y)
      targetRef.current = over !== null && over !== d.from ? over : null
      setTarget(targetRef.current)
    }
  }

  const up = (): void => {
    const d = dragRef.current
    if (d && d.moved) {
      if (d.kind === 'swap' && targetRef.current !== null) swap(d.from, targetRef.current)
      if (d.kind === 'move' || d.kind === 'resize') place(d.from, d.live)
    }
    dragRef.current = null
    targetRef.current = null
    setDrag(null)
    setTarget(null)
  }

  /** Grid mode: exchange the two cameras' places on the wall. */
  const swap = (fromCell: number, toCell: number): void => {
    const from = cells[fromCell]
    const to = cells[toCell]
    if (!from || !to || from.cam === to.cam) return
    const a = cameras[from.cam]
    const b = cameras[to.cam]
    // Swap in the full list, matched by id — the enabled list is a filtered
    // view, so enabled positions cannot index it directly.
    const next = config.cameras.map((c) => (c.id === a.id ? b : c.id === b.id ? a : c))
    const main =
      config.mainCameraIndex === from.cam
        ? to.cam
        : config.mainCameraIndex === to.cam
          ? from.cam
          : config.mainCameraIndex
    onPatch({ cameras: next, mainCameraIndex: main })
  }

  /** Spotlight mode: pin this mini wherever it was dropped. */
  const place = (cellIndex: number, rect: ThumbRect): void => {
    const cell = cells[cellIndex]
    const camera = cell && cameras[cell.cam]
    if (!camera) return
    onPatch({ spotlightRects: { ...config.spotlightRects, [camera.id]: rect } })
  }

  /** Double-click a mini: it takes the background, the old background joins the minis. */
  const promote = (cellIndex: number): void => {
    const cell = cells[cellIndex]
    if (!cell || cell.main || locked || !spotlight) return
    onPatch({ mainCameraIndex: cell.cam })
  }

  /** Mini ids bottom→top as currently drawn — cells already follow the plan's order. */
  const miniIds = cells.filter((c) => !c.main).map((c) => cameras[c.cam]?.id ?? '')

  /** Each source fills its box its own way; '' falls back to the defaults. */
  const setFit = (id: string, fit: CameraFit): void =>
    onPatch({ cameras: config.cameras.map((c) => (c.id === id ? { ...c, fit } : c)) })

  /**
   * Move one mini through the stack. Saving the full current sequence (not a
   * delta) makes the result independent of how ids were ranked before.
   */
  const layerMove = (id: string, delta: number): void => {
    const order = [...miniIds]
    const at = order.indexOf(id)
    const to = at + delta
    if (at < 0 || to < 0 || to >= order.length) return
    order.splice(at, 1)
    order.splice(to, 0, id)
    onPatch({ spotlightOrder: order })
  }

  const dragging = drag?.moved ? drag.from : null

  return (
    <div className="field">
      <div className="comp-head">
        <label>
          Composition —{' '}
          {spotlight ? 'drag minis anywhere, drag the corner to resize' : 'drag a view to rearrange'}
        </label>
        {spotlight && (
          <span className="comp-tools">
            <label className="check comp-snap">
              <input type="checkbox" checked={snap} onChange={(e) => setSnap(e.target.checked)} />
              <span>Snap to grid</span>
            </label>
            {placed && !locked && (
              <button
                className="btn small"
                onClick={() => onPatch({ spotlightRects: {} })}
                title="Forget every hand-placed position and return the minis to the automatic strip"
              >
                Auto arrange
              </button>
            )}
          </span>
        )}
      </div>
      <div
        ref={boardRef}
        className={[
          'comp-board',
          locked ? 'locked' : '',
          drag?.moved && drag.kind !== 'swap' && snap ? 'gridded' : ''
        ]
          .filter(Boolean)
          .join(' ')}
        style={
          {
            height: boardH,
            '--snap': `${SNAP * scale}px`
          } as React.CSSProperties
        }
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
      >
        {cells.map((cell, i) => {
          const camera = cameras[cell.cam]
          if (!camera) return null
          const live = dragging === i && drag ? drag.live : cell
          return (
            <div
              key={`${camera.id}-${cell.main ? 'main' : 'tile'}`}
              className={[
                'comp-cell',
                cell.main ? 'main' : '',
                cell.custom ? 'placed' : '',
                dragging === i ? 'lifting' : '',
                target === i ? 'drop' : ''
              ]
                .filter(Boolean)
                .join(' ')}
              style={{
                left: live.x * scale,
                top: live.y * scale,
                width: live.w * scale,
                height: live.h * scale
              }}
              onPointerDown={(e) => down(e, i)}
              onDoubleClick={() => promote(i)}
            >
              <span className="comp-no">{cell.main ? '★' : cell.cam + 1}</span>
              <span className="comp-name">{camera.name}</span>
              <span className="comp-kind">{KIND_TAGS[camera.kind]}</span>
              {spotlight && !cell.main && !locked && (
                <span
                  className="comp-resize"
                  onPointerDown={(e) => begin(e, i, 'resize')}
                  title="Drag to resize"
                />
              )}
            </div>
          )
        })}
        {drag?.moved && drag.kind === 'swap' && cells[drag.from] && (
          <div className="comp-ghost" style={{ left: drag.px, top: drag.py }}>
            {cameras[cells[drag.from].cam]?.name}
          </div>
        )}
        {drag?.moved && drag.kind !== 'swap' && (
          <div className="comp-coords">
            {drag.live.w}×{drag.live.h} @ {drag.live.x},{drag.live.y}
          </div>
        )}
      </div>
      {spotlight && cells.length > 1 && (
        <div className="comp-layers">
          <span className="comp-layers-title">Mini layers — top first</span>
          {[...cells]
            .filter((c) => !c.main)
            .reverse()
            .map((cell) => {
              const camera = cameras[cell.cam]
              if (!camera) return null
              const at = miniIds.indexOf(camera.id)
              return (
                <div key={camera.id} className="comp-layer-row">
                  <span className="comp-no">{cell.cam + 1}</span>
                  <span className="comp-name">{camera.name}</span>
                  {cell.custom && <span className="comp-kind">placed</span>}
                  <select
                    className="comp-fit"
                    value={camera.fit ?? ''}
                    disabled={locked}
                    title="How this source fills its box"
                    onChange={(e) => setFit(camera.id, e.target.value as CameraFit)}
                  >
                    <option value="">Cover — crop (default)</option>
                    <option value="contain">Contain — letterbox</option>
                    <option value="cover">Cover — crop</option>
                    <option value="stretch">Stretch</option>
                  </select>
                  <button
                    className="btn small"
                    disabled={locked || at === miniIds.length - 1}
                    title="Bring forward — draws over the minis below it"
                    onClick={() => layerMove(camera.id, 1)}
                  >
                    ▲
                  </button>
                  <button
                    className="btn small"
                    disabled={locked || at === 0}
                    title="Send backward"
                    onClick={() => layerMove(camera.id, -1)}
                  >
                    ▼
                  </button>
                </div>
              )
            })}
          {(() => {
            // The background deserves the same control; its default is the
            // Layout tab's global Fit rather than the minis' cover.
            const bg = cells.find((c) => c.main)
            const camera = bg && cameras[bg.cam]
            if (!camera) return null
            return (
              <div className="comp-layer-row comp-layer-bg">
                <span className="comp-no">★</span>
                <span className="comp-name">{camera.name} — background</span>
                <select
                  className="comp-fit"
                  value={camera.fit ?? ''}
                  disabled={locked}
                  title="How the background fills the canvas"
                  onChange={(e) => setFit(camera.id, e.target.value as CameraFit)}
                >
                  <option value="">Default — the Fit setting above</option>
                  <option value="contain">Contain — letterbox</option>
                  <option value="cover">Cover — crop</option>
                  <option value="stretch">Stretch</option>
                </select>
              </div>
            )
          })()}
        </div>
      )}
      <p className="hint">
        {locked
          ? 'Stop the stream to rearrange the composition.'
          : spotlight
            ? 'Each mini is independent: drag it anywhere, resize it by its corner, double-click to put it in the spotlight. Minis never touched stay in the automatic strip; when minis overlap, the layer list decides who draws on top.'
            : 'Drag a tile onto another to swap their places on the wall.'}
      </p>
    </div>
  )
}
